// src/pages/public/MeetTheTeam.js
import React, { useLayoutEffect, useRef } from "react";
import "./index.css";
import imgBrian from "../../../../shared/assets/images/bio-brian.png";
import imgSarah from "../../../../shared/assets/images/bio-sarah.png";
import imgGianna from "../../../../shared/assets/images/bio-gianna.png";
import { gsap, runGsapOnce } from "../../../../shared/utils/gsap";
import { motionTokens } from "../../../../shared/utils/motion";

const team = [
  {
    name: "Gianna Clark",
    role: "Founder & CEO",
    photo: imgGianna,
    photoWidth: 2048,
    photoHeight: 1664,
    focus: "Reimagining content creation.",
    description: [
      "Gianna is the Founder and CEO of Refanno. As an advertising creative director/copywriter she's led content creation, positioning, and messaging for a variety of verticals from entertainment to lifestyle brands including CPG, home decor, and culinary before pivoting to the pharmaceutical sector.",
      "",
      "She became intrigued by the absence of a platform uniquely designed to bridge art and content, messaging and marketers - a repeating pain point in all businesses that begged for some attention, especially in the highly regulated world of pharmaceutical marketing.",
    ].join("\n"),
    quote:
      "I love the challenge of developing a content tool that's elegant, efficient and flexible. And I'm fortunate to have a team with the imagination, passion and talent to make it happen.",
  },
  {
    name: "Sarah Moran",
    role: "Chief Strategy Officer",
    photo: imgSarah,
    photoWidth: 2487,
    photoHeight: 1658,
    focus: "Uniting vision, strategy, and results to drive growth.",
    description:
      "Sarah is the Chief Strategy Officer at Refanno. A strategic marketer, she's worked with everyone from Fortune 500 giants like Verizon, Pfizer, and IPG, to scrappy startups - helping them grow their vision, business, and relationships. Passionate about fitness and the arts, in her free time, she's either on a long run or serving on the board of Chicago's Filament Theatre.",
    quote:
      "It's a marathon, not a sprint. I dream big and set goals and processes to achieve those dreams... one step at a time.",
  },
  {
    name: "Brian Boyd",
    role: "Chief Technology Officer",
    photo: imgBrian,
    photoWidth: 1170,
    photoHeight: 1059,
    focus: "Making complex feel simple.",
    description:
      "As Refanno's CTO, Brian designs and builds systems that emphasize clarity, reliability, and speed. He previously built large-scale financial data applications at S&P Global, where he developed a disciplined approach to engineering. He combines technical precision with a focus on simplicity and performance. When not coding, you'll find him skiing fresh powder or cycling throughout New York City.",
    quote:
      "I'm here to remove the 'why is this so hard?' moments from your workflow. If it's simple and intuitive, you're free to focus on what matters most: creating great content.",
  },
];

export default function MeetTheTeam() {
  const sectionRef = useRef(null);
  const headerRef = useRef(null);
  const cardRefs = useRef([]);

  useLayoutEffect(() => {
    if (!sectionRef.current) return undefined;

    return runGsapOnce("about-us-page", () => {
      const ctx = gsap.context(() => {
        const headerChildren = headerRef.current?.children || [];
        const cards = cardRefs.current.filter(Boolean);

        const introTimeline = gsap.timeline({
          paused: true,
          defaults: { ease: "power2.out" },
        });

        if (headerChildren.length) {
          gsap.set(headerChildren, {
            opacity: 0,
            y: 28,
            filter: "blur(8px)",
          });
          introTimeline.to(headerChildren, {
            opacity: 1,
            y: 0,
            filter: "blur(0px)",
            duration: motionTokens.duration650,
            stagger: motionTokens.duration100,
          });
        }

        if (cards.length) {
          gsap.set(cards, {
            opacity: 0,
            y: 34,
            filter: "blur(8px)",
          });
          cards.forEach((card, index) => {
            const portrait = card.querySelector(".about-portrait");
            const storyChildren =
              card.querySelector(".about-story")?.children || [];
            const fromX = index % 2 === 0 ? -32 : 32;

            gsap.set(card, { x: fromX });
            if (portrait) {
              gsap.set(portrait, {
                opacity: 0,
                scale: 0.96,
                filter: "blur(6px)",
              });
            }
            if (storyChildren.length) {
              gsap.set(storyChildren, {
                opacity: 0,
                y: 18,
              });
            }

            const cardTimeline = gsap.timeline({
              scrollTrigger: {
                trigger: card,
                start: "top 84%",
                once: true,
              },
              defaults: { ease: "power2.out" },
            });

            cardTimeline.to(card, {
              opacity: 1,
              x: 0,
              y: 0,
              filter: "blur(0px)",
              duration: motionTokens.duration700,
            });

            if (portrait) {
              cardTimeline.to(
                portrait,
                {
                  opacity: 1,
                  scale: 1,
                  filter: "blur(0px)",
                  duration: motionTokens.duration500,
                },
                "-=0.5",
              );
            }

            if (storyChildren.length) {
              cardTimeline.to(
                storyChildren,
                {
                  opacity: 1,
                  y: 0,
                  duration: motionTokens.duration450,
                  stagger: motionTokens.duration80,
                },
                "-=0.38",
              );
            }
          });
        }

        introTimeline.play();
      }, sectionRef);

      return () => ctx.revert();
    });
  }, []);

  return (
    <section
      className="about-us-section"
      ref={sectionRef}
      aria-labelledby="about-us-title"
    >
      <div className="page-top-accent" aria-hidden />
      <div className="page-backdrop" aria-hidden />
      <div className="about-us-container">
        <header className="about-us-header" ref={headerRef}>
          <h1
            id="about-us-title"
            className="public-section-title about-us-title"
          >
            Meet the Team
          </h1>
        </header>

        <div className="about-team-grid">
          {team.map((member, index) => (
            <article
              key={member.name}
              className={`about-team-member${
                index % 2 === 1 ? " about-team-member--reverse" : ""
              }`}
              ref={(el) => {
                cardRefs.current[index] = el;
              }}
            >
              <figure className="about-portrait">
                <img
                  src={member.photo}
                  alt={member.name}
                  width={member.photoWidth}
                  height={member.photoHeight}
                  loading="lazy"
                  decoding="async"
                />
              </figure>
              <div className="about-story">
                <div>
                  <h2 className="about-name">{member.name}</h2>
                  <p className="about-role">{member.role}</p>
                </div>
                <div className="about-focus">
                  <span className="about-focus-label">Focus</span>
                  <p className="about-focus-text">{member.focus}</p>
                </div>
                <p className="about-description">{member.description}</p>
                {member.quote && (
                  <p className="about-member-quote">
                    &ldquo;{member.quote}&rdquo;
                  </p>
                )}
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}
